import { useRef, useState } from 'react';
import { TooltipOnTruncated } from '@components/TooltipOnTruncated';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { useClosePopoverOnScrollOut } from '@hooks/use-close-popover-on-scroll-out';
import { Icon } from '@mdi/react';
import clsx from 'clsx';

import { MultiSelectDetailsPanel } from './MultiSelectDetailsPanel';

export interface MultiSelectCellProps {
  entityName: string;
  entityNameSingular?: string;
  icon: string;
  isLoading?: boolean;
  items: Array<{ id: string | number; name: string }>;
  label?: string;
  maxWidth?: string;
  onItemClick?: (id: string | number) => void;
  textColor?: string;
}

const singleItemBadgeClasses = clsx(
  'inline-flex',
  'items-center',
  'gap-x-1.5',
  'whitespace-nowrap',
  'rounded-xl',
  'bg-gray-100',
  'px-3',
  'py-1',
  'text-xs',
  'font-medium',
  'max-w-8',
  'sm:max-w-8',
  'md:max-w-12',
  'lg:max-w-[3.2rem]',
  'xl:max-w-24',
  '2xl:max-w-40',
);

const countBadgeClasses = clsx(
  'inline-flex',
  'items-center',
  'gap-x-1.5',
  'whitespace-nowrap',
  'rounded-xl',
  'bg-gray-100',
  'px-3',
  'py-1',
  'text-xs',
  'font-medium',
  'cursor-pointer',
  'hover:bg-gray-200',
  'transition-colors',
);

export function MultiSelectCell({
  entityName,
  icon,
  isLoading = false,
  items,
  label = 'Assigned to',
  maxWidth,
  onItemClick,
  textColor = 'text-blue-600',
}: MultiSelectCellProps) {
  const [open, setOpen] = useState(false);
  const popoverTriggerRef = useRef(null);
  useClosePopoverOnScrollOut(popoverTriggerRef, open, setOpen);

  if (!items || items.length === 0) {
    return null;
  }

  if (items.length === 1) {
    const item = items[0];
    return (
      <span className={clsx(singleItemBadgeClasses, textColor)} style={maxWidth ? { maxWidth } : undefined}>
        <TooltipOnTruncated tooltipContent={item.name}>{item.name}</TooltipOnTruncated>
        <Icon path={icon} size={0.5} className="inline-block flex-shrink-0" />
      </span>
    );
  }

  const displayText = `${items.length} ${entityName}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span ref={popoverTriggerRef} className={clsx(countBadgeClasses, textColor)}>
          <span>{displayText}</span>
          <Icon path={icon} size={0.5} className="inline-block flex-shrink-0" />
        </span>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0">
        <MultiSelectDetailsPanel
          label={label}
          items={items}
          isLoading={isLoading}
          onItemClick={onItemClick}
          showIcon={!!onItemClick}
        />
      </PopoverContent>
    </Popover>
  );
}
